import { useEffect, useState } from 'react';
import { Box, Card, Grid, FormControl, Typography } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { InputText } from '@/components/shared/InputText';
import { useAppDispatch } from '@/lib/hooks';
import {
    SecondaryButtonRegular,
    PrimaryButton
} from '@/components/shared/Button';
import { CompanyDetailsResponse } from '@/models/payment';
import { CompanySettingProps, approvalTypes } from '@/models/company';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { Dropdown } from '@/components/shared/Dropdown';
import { updateCompanyInfo } from '@/lib/slices/paymentsSlice';
import {
    Title,
    SubTitle,
    TextMDSemiBold,
    SMRegular,
    FONT_WEIGHT
} from '@/typography/Typography';
import { ConnectPaymentService } from './common/ConnectPaymentService';
import styled from '@emotion/styled';
import { WithdrawalLimitsSettings } from '@/components/payments/WithdrawalLimitsSettings';
import { AutoTopUpSettings } from '@/components/payments/AutoTopUpSettings';
import {
    GridContainer,
    IntegrationCard,
    IntegrationCardPadding
} from './componants/Base';
import { IOSSwitch } from '@/components/shared/Switch';
import { LowBalanceSettings } from '@/components/payments/LowBalanceSettings';

export const ButtonContainer = styled(Box)`
    display: flex;
    justify-content: end;
    width: 100%;
    border-top: 1px solid #d0d5dd;
    padding: 24px;
`;

const ConnectedBankContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    marginTop: '16px',
    marginBottom: '16px',
    backgroundColor: '#F0F9F0',
    border: '1px solid #4CAF50',
    borderRadius: '8px',
    '& .bank-info': {
        marginLeft: '16px',
        flex: 1
    }
}));

const RemoveButton = styled('button')({
    backgroundColor: 'transparent',
    border: 'none',
    color: '#DC3545',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'underline',
    '&:hover': {
        textDecoration: 'none'
    }
});

export const PaymentSetting = ({
    companyDetails,
    reloadDetails
}: CompanySettingProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { setAlert } = useNotificationsSnackbar();
    const [companyUpdateData, setCompanyUpdateData] =
        useState<CompanyDetailsResponse>({});
    const [lowBalanceEmailError, setLowBalanceEmailError] =
        useState<boolean>(false);
    const [
        lowBalanceNotificationThresholdError,
        setLowBalanceNotificationThresholdError
    ] = useState<boolean>(false);

    useEffect(() => {
        if (companyDetails?.data) {
            setCompanyUpdateData({
                allowancePercentage: companyDetails?.data?.allowancePercentage,
                latestWithdrawalDay: companyDetails?.data?.latestWithdrawalDay,
                salaryAvailableHour: companyDetails?.data?.salaryAvailableHour,
                hourlyEmployeesPayrollStartDay:
                    companyDetails?.data?.hourlyEmployeesPayrollStartDay,
                notifyPendingWithdrawalsEmail:
                    companyDetails?.data?.notifyPendingWithdrawalsEmail,
                transactionEmployeeText:
                    companyDetails?.data?.transactionEmployeeText,
                paymentProvider: companyDetails?.data?.paymentProvider,
                transactionExecutionType:
                    companyDetails?.data?.transactionExecutionType,
                approvalType: companyDetails?.data?.approvalType,
                transactionEmployerText:
                    companyDetails?.data?.transactionEmployerText,
                companyEmail: companyDetails?.data?.companyEmail,
                enableCustomTransactionMessages:
                    companyDetails?.data?.enableCustomTransactionMessages,
                lowBalanceEmail: companyDetails?.data?.lowBalanceEmail,
                lowBalanceNotificationThreshold:
                    companyDetails?.data?.lowBalanceNotificationThreshold
            });
        }
    }, [companyDetails]);

    const isValidLowBalanceData = (data: CompanyDetailsResponse) => {
        if (!data.lowBalanceNotificationThreshold) {
            setLowBalanceNotificationThresholdError(true);
            return false;
        } else {
            setLowBalanceNotificationThresholdError(false);
        }

        if (!data.lowBalanceEmail) {
            setLowBalanceEmailError(true);
            return false;
        } else {
            setLowBalanceEmailError(false);
        }

        return true;
    };

    const handleLowBalance = () => {
        if (isValidLowBalanceData(companyUpdateData)) {
            dispatch(
                updateCompanyInfo({
                    request: companyUpdateData
                })
            );
            setAlert('Low balance section updated successfully.', 'success');
        }
    };

    const handlePayoutMethod = () => {
        dispatch(updateCompanyInfo({ request: companyUpdateData }));
        setAlert('Payout method updated successfully.', 'success');
    };

    const resetPaymentSetting = (sectionName: string) => {
        if (sectionName === 'payoutMethod') {
            setCompanyUpdateData({
                name: companyDetails?.data?.name,
                nameAlias: companyDetails?.data?.nameAlias,
                vatNumber: companyDetails?.data?.vatNumber,
                currency: companyDetails?.data?.currency,
                timeZoneInfoId: companyDetails?.data?.timeZoneInfoId
            });
        } else if (sectionName === 'lowBalance') {
            setCompanyUpdateData({
                lowBalanceEmail: companyDetails?.data?.lowBalanceEmail || '',
                lowBalanceNotificationThreshold:
                    companyDetails?.data?.lowBalanceNotificationThreshold || 0
            });
        } else if (sectionName === 'transactionSpeed') {
            setCompanyUpdateData({
                transactionExecutionType:
                    companyDetails?.data?.transactionExecutionType
            });
        } else if (sectionName === 'bankDetailsCompany') {
            setCompanyUpdateData({
                transactionEmployerText:
                    companyDetails?.data?.transactionEmployerText
            });
        } else if (sectionName === 'paymentApprovalType') {
            setCompanyUpdateData({
                approvalType: companyDetails?.data?.approvalType
            });
        }
    };

    return (
        <>
            <Grid container mt={2} justifyContent={'space-between'}>
                <Grid item xs={3}>
                    <Title>{t('payment_service')}</Title>
                    <SubTitle>{t('payment_service_description')}</SubTitle>
                </Grid>
                <Grid item xs={8.8}>
                    <IntegrationCard>
                        <IntegrationCardPadding>
                            <ConnectPaymentService
                                companyDetails={companyDetails}
                                reloadList={reloadDetails}
                            />
                        </IntegrationCardPadding>
                    </IntegrationCard>
                </Grid>
            </Grid>
            {companyDetails?.data?.paymentProvider && (
                <Grid container mt={2} justifyContent={'space-between'}>
                    <Grid item xs={3}></Grid>
                    <Grid item xs={8.8}>
                        {companyDetails?.data?.paymentProvider === 'None' && (
                            <Card>
                                <GridContainer container>
                                    <Title>{t('approval_type')}</Title>
                                    <FormControl fullWidth>
                                        <Dropdown
                                            label=""
                                            choices={approvalTypes?.map(
                                                (item) => ({
                                                    label: item,
                                                    value: item
                                                })
                                            )}
                                            value={
                                                companyUpdateData?.approvalType
                                            }
                                            onChange={(val: any) => {
                                                setCompanyUpdateData({
                                                    ...companyUpdateData,
                                                    approvalType: val
                                                });
                                            }}
                                            listContainerStyle={{
                                                fontWeight: 400
                                            }}
                                        />
                                    </FormControl>
                                    <SubTitle>
                                        {t('approval_type_note')}
                                    </SubTitle>
                                </GridContainer>

                                <ButtonContainer>
                                    <SecondaryButtonRegular
                                        onClick={() =>
                                            resetPaymentSetting(
                                                'paymentApprovalType'
                                            )
                                        }
                                    >
                                        {t('cancel')}
                                    </SecondaryButtonRegular>
                                    <PrimaryButton
                                        onClick={handlePayoutMethod}
                                        sx={{ marginLeft: '6px !important' }}
                                        size="medium"
                                    >
                                        {t('save_changes')}
                                    </PrimaryButton>
                                </ButtonContainer>
                            </Card>
                        )}

                        {companyDetails?.data
                            ?.enableCustomTransactionMessages &&
                            (companyDetails?.data?.paymentProvider === 'Aiia' ||
                                companyDetails?.data?.paymentProvider ===
                                    'DanskeBank') && (
                                <Card>
                                    <GridContainer container>
                                        <InputText
                                            value={
                                                companyUpdateData?.transactionEmployerText
                                            }
                                            onChange={(val: any) => {
                                                setCompanyUpdateData({
                                                    ...companyUpdateData,
                                                    transactionEmployerText:
                                                        val.target.value ?? ''
                                                });
                                            }}
                                            label={t('bank_details_company')}
                                            placeholder="This is the placeholder default transaction text"
                                        />
                                        <SubTitle>
                                            {t('bank_details_company_note')}
                                        </SubTitle>
                                    </GridContainer>

                                    <ButtonContainer>
                                        <SecondaryButtonRegular
                                            onClick={() =>
                                                resetPaymentSetting(
                                                    'bankDetailsCompany'
                                                )
                                            }
                                        >
                                            {t('cancel')}
                                        </SecondaryButtonRegular>
                                        <PrimaryButton
                                            onClick={handlePayoutMethod}
                                            sx={{
                                                marginLeft: '6px !important'
                                            }}
                                            size="medium"
                                        >
                                            {t('save_changes')}
                                        </PrimaryButton>
                                    </ButtonContainer>
                                </Card>
                            )}
                    </Grid>
                </Grid>
            )}

            {companyDetails?.data?.paymentProvider === 'None' &&
                companyDetails?.data?.paymentSource === 'InstaPaidAccount' && (
                    <LowBalanceSettings currentCompany={companyDetails.data} />
                )}

            {companyDetails?.data?.paymentProvider === 'Aiia' && (
                <Grid
                    container
                    mt={3}
                    pb={3}
                    justifyContent={'space-between'}
                    borderTop={'1px solid #D0D5DD'}
                    pt={5}
                >
                    <Grid item xs={3}>
                        <Title>{t('transaction_type')}</Title>
                    </Grid>
                    <Grid item xs={8.8}>
                        <Card>
                            <GridContainer container>
                                <Title>{t('transaction_execution_type')}</Title>
                                <FormControl fullWidth>
                                    <Dropdown
                                        label=""
                                        choices={['Normal', 'Instant'].map(
                                            (item) => ({
                                                label: item,
                                                value: item
                                            })
                                        )}
                                        value={
                                            companyUpdateData?.transactionExecutionType
                                        }
                                        onChange={(val: any) => {
                                            setCompanyUpdateData({
                                                ...companyUpdateData,
                                                transactionExecutionType: val
                                            });
                                        }}
                                        listContainerStyle={{ fontWeight: 400 }}
                                    />
                                </FormControl>
                                <SubTitle>
                                    {t('transaction_execution_type_note')}
                                </SubTitle>
                            </GridContainer>
                            <ButtonContainer>
                                <SecondaryButtonRegular
                                    onClick={() =>
                                        resetPaymentSetting('transactionSpeed')
                                    }
                                >
                                    {t('cancel')}
                                </SecondaryButtonRegular>
                                <PrimaryButton
                                    onClick={handlePayoutMethod}
                                    sx={{ marginLeft: '6px !important' }}
                                    size="medium"
                                >
                                    {t('save_changes')}
                                </PrimaryButton>
                            </ButtonContainer>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <WithdrawalLimitsSettings currentCompany={companyDetails?.data} />
        </>
    );
};
